import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { Gallery, Button, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem } from '@patternfly/react-core';

import useAPI from 'services/useAPI';
import useAlerts from 'utils/hooks/useAlerts';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';

import DeleteAsset from './DeleteAsset';
import { DATA_SOURCE } from './WizardConstants';
import AssetCard from './components/Cards/AssetCard';
import ProviderClientCard from './components/Cards/ProviderClientCard';
import IothubDeviceCard from './components/Cards/IothubDeviceCard';
import ProvisioningCard from './components/Cards/ProvisioningCard';
import { Icons } from 'components/Common';
import createQueryRequest from 'services/createQueryRequest';
import BreadcrumbSkeleton from 'components/Breadcrumb/BreadcrumbSkeleton';
import { useQuery } from '@tanstack/react-query';
import { useAuthService } from 'services/AuthService/AuthService';
import { useUserPermissionQuery, useUserPermissionsQueries } from 'services/Permissions/queries';
import { useAssetQuery, useAssetsAndProvidersQuery } from './queries';
import { useProviderClient } from 'components/Admin/Providers/queries';


export const AssetName = ({ assetId }) => {
  const api = useAPI();
  const assetQuery = useQuery(['asset', assetId], createQueryRequest(api.core.getAsset));
  return assetQuery.isLoading ? <BreadcrumbSkeleton /> : (assetQuery.data?.displayName ?? '');
};

const AdminViewAssetPage = ({ breadcrumbs }) => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuthService();
  const { assetId } = useParams();
  const [alerts] = useAlerts();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [assetInfo, setAssetInfo] = useState({});

  // TODO: need to be rewritten for multiple providerClients
  const assetsAndProvidersQuery = useAssetsAndProvidersQuery(assetId);
  const assetQuery = useAssetQuery(assetId);
  const refreshAssetInfo = () => {
    assetQuery.refetch();
  };

  const providerClientQuery = useProviderClient(assetInfo?.providerClient?.provider?.id, assetInfo?.providerClient?.id);
  const refreshProviderClient = () => {
    providerClientQuery.query.refetch();
  };

  const providerClientIds = useMemo(() => {
    if (assetInfo?.providers) {
      return _.map(assetInfo?.providers, provider => provider.id + '::' + assetInfo.providerClient.id);
    }
    return [];
  }, [assetInfo?.providers, assetInfo?.providerClient?.id]);

  const assetPermissionQuery = useUserPermissionQuery(user?.userId, 'asset', assetId, 'manage');
  const providerPermissionsQueries = useUserPermissionsQueries(user?.userId, 'provider_client', providerClientIds, 'manage');

  useEffect(() => {
    if (assetsAndProvidersQuery?.data) {
      setAssetInfo({
        ...assetsAndProvidersQuery.data,
        dataSource: (_.has(assetsAndProvidersQuery.data, 'providerClients')) ? DATA_SOURCE.provider : DATA_SOURCE.asset,
        providerClient: {
          ...(_.first(assetsAndProvidersQuery.data.providerClients)),
          provider: _.first(assetsAndProvidersQuery.data.providers)
        }

      });
    }
  }, [assetsAndProvidersQuery?.data]);

  useEffect(() => {
    if (assetQuery?.data) {
      setAssetInfo(oldInfo => {
        return {
          ...oldInfo,
          asset: {
            ...oldInfo.asset,
            displayName: assetQuery.data.displayName,
            description: assetQuery.data.description
          }
        };
      });
    }
  }, [assetQuery?.data]);

  useEffect(() => {
    if (providerClientQuery?.providerClient) {
      setAssetInfo(oldInfo => {
        return {
          ...oldInfo,
          providerClient: {
            ...oldInfo.providerClient,
            displayName: providerClientQuery.providerClient.displayName,
            description: providerClientQuery.providerClient.description
          }
        };
      });
    }
  }, [providerClientQuery?.providerClient]);

  const onMigrateAsset = useCallback(() => {
    history.push({ pathname: `${location.pathname}/migrate` });
  }, [history, location.pathname]);

  const contentToolbar = useMemo(() => {
    return (
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup>
            <ToolbarItem>
              <Button onClick={onMigrateAsset}>Migrate Asset</Button>
            </ToolbarItem>
            <ToolbarItem>
              <Button onClick={() => setConfirmDeleteItem(assetInfo.asset)} icon={<Icons.Delete />} variant="danger">Delete Asset</Button>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    );
  }, [assetInfo.asset, onMigrateAsset]);

  return (
    <ContentPage breadcrumb={breadcrumbs} toolbar={contentToolbar} help={'admin/assets/view'}>
      { alerts }
      { /* <Flex spaceItems={{ default: 'spaceItems2xl' }}>
        <FlexItem>
          <Button onClick={onMigrateAsset}>Migrate Asset</Button>
        </FlexItem>
        <FlexItem >
          <Button onClick={() => setConfirmDeleteItem(assetInfo.asset)} icon={<Icons.Delete />} variant="danger">Delete Asset</Button>
        </FlexItem>
      </Flex> */ }
      <div style={{ height: '100%', overflow: 'auto' }}>
        <Gallery
          hasGutter
          className={'detail-gallery'}
        >
          <AssetCard assetInfo={assetInfo.asset} isLoading={assetsAndProvidersQuery?.pending || assetQuery?.pending} hasEdit={assetPermissionQuery?.data?.[0]?.result ?? false} updateAssetInfo={refreshAssetInfo} />
          <ProviderClientCard providerClientInfo={assetInfo.providerClient} isLoading={assetsAndProvidersQuery?.pending || providerClientQuery?.query?.pending} hasEdit={_.some(providerPermissionsQueries, res => res?.data?.[0]?.result) ?? false} updateProviderClientInfo={refreshProviderClient} />
          <IothubDeviceCard iotDeviceInfo={assetInfo?.providerClient?.providerData} isLoading={assetsAndProvidersQuery?.pending} />
          <ProvisioningCard provisionData={assetInfo?.providerClient?.provisioningData} isLoading={assetsAndProvidersQuery?.pending} updateProvisionData={undefined} />
        </Gallery>
      </div>
      <DeleteAsset
        confirmDeleteItem={confirmDeleteItem}
        onDelete={() => history.push('/admin/assets')}
        cancelDelete={() => {
          setConfirmDeleteItem(null);
        }}
      />
    </ContentPage>
  );
};

export default AdminViewAssetPage;
